import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Card,
  Col,
  Form,
  Input,
  InputNumber,
  Row,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import { EditOutlined, GlobalOutlined, PlusOutlined, SearchOutlined } from '@ant-design/icons';
import CustomDrawer from 'components/shared-components/CustomDrawer';
import Flex from 'components/shared-components/Flex';
import apiClient from 'services/apiClient';
import { useMyContext } from 'Context/MyContextProvider';
import { ROW_GUTTER } from 'constants/ThemeConstant';
import dayjs from 'dayjs';

const { Text } = Typography;

const DEFAULT_FORM_VALUES = {
  currency: 'INR',
  is_active: true,
  effective_from: dayjs().format('YYYY-MM-DD'),
};

const formatPrice = (value) => {
  if (value === null || value === undefined || value === '') {
    return '—';
  }

  return Number(value).toFixed(4);
};

const MetaPricing = () => {
  const { api, authToken } = useMyContext();
  const [form] = Form.useForm();
  const [rates, setRates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [searchText, setSearchText] = useState('');

  const loadRates = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiClient.get(`${api}meta-pricing`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      setRates(response?.data?.data || []);
    } catch (error) {
      message.error(error?.response?.data?.message || 'Failed to load Meta pricing');
    } finally {
      setLoading(false);
    }
  }, [api, authToken]);

  useEffect(() => {
    loadRates();
  }, [loadRates]);

  const filteredRates = useMemo(() => {
    const query = searchText.trim().toLowerCase();
    if (!query) {
      return rates;
    }

    return rates.filter((row) =>
      [row.country_iso2, row.market_name, row.currency]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(query))
    );
  }, [rates, searchText]);

  const closeDrawer = () => {
    setDrawerOpen(false);
    setEditingRecord(null);
    form.resetFields();
  };

  const openCreateDrawer = () => {
    setEditingRecord(null);
    form.setFieldsValue(DEFAULT_FORM_VALUES);
    setDrawerOpen(true);
  };

  const openEditDrawer = (record) => {
    setEditingRecord(record);
    form.setFieldsValue({
      country_iso2: record.country_iso2,
      market_name: record.market_name || '',
      currency: record.currency || 'INR',
      marketing_price: record.marketing_price,
      utility_price: record.utility_price,
      authentication_price: record.authentication_price,
      authentication_intl_price: record.authentication_intl_price,
      effective_from: record.effective_from,
      is_active: record.is_active === true || record.is_active === 1,
    });
    setDrawerOpen(true);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const payload = {
        country_iso2: values.country_iso2?.trim().toUpperCase(),
        market_name: values.market_name?.trim() || null,
        currency: values.currency?.trim().toUpperCase() || 'INR',
        marketing_price: values.marketing_price,
        utility_price: values.utility_price,
        authentication_price: values.authentication_price,
        authentication_intl_price: values.authentication_intl_price ?? null,
        effective_from: values.effective_from,
        is_active: values.is_active === true,
      };

      setSaving(true);

      if (editingRecord?.id) {
        await apiClient.put(`${api}meta-pricing/${editingRecord.id}`, payload, {
          headers: { Authorization: `Bearer ${authToken}` },
        });
        message.success('Meta pricing updated');
      } else {
        await apiClient.post(`${api}meta-pricing`, payload, {
          headers: { Authorization: `Bearer ${authToken}` },
        });
        message.success('Meta pricing added');
      }

      closeDrawer();
      loadRates();
    } catch (error) {
      if (error?.errorFields) {
        return;
      }
      const validationMessage = error?.response?.data?.errors
        ? Object.values(error.response.data.errors).flat().join(', ')
        : null;
      message.error(validationMessage || error?.response?.data?.message || 'Failed to save Meta pricing');
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    {
      title: 'Country',
      dataIndex: 'country_iso2',
      width: 100,
      render: (iso2) => <Tag>{iso2}</Tag>,
    },
    {
      title: 'Market',
      dataIndex: 'market_name',
      render: (value) => value || '—',
    },
    {
      title: 'Currency',
      dataIndex: 'currency',
      width: 90,
    },
    {
      title: 'Marketing',
      dataIndex: 'marketing_price',
      render: formatPrice,
    },
    {
      title: 'Utility',
      dataIndex: 'utility_price',
      render: formatPrice,
    },
    {
      title: 'Authentication',
      dataIndex: 'authentication_price',
      render: formatPrice,
    },
    {
      title: 'Auth Intl',
      dataIndex: 'authentication_intl_price',
      render: formatPrice,
    },
    {
      title: 'Effective From',
      dataIndex: 'effective_from',
      width: 130,
      render: (value) => (value ? dayjs(value).format('DD MMM YYYY') : '—'),
    },
    {
      title: 'Active',
      dataIndex: 'is_active',
      width: 90,
      render: (active) => (
        <Tag color={active ? 'green' : 'default'}>{active ? 'Yes' : 'No'}</Tag>
      ),
    },
    {
      title: 'Action',
      key: 'action',
      width: 90,
      render: (_, record) => (
        <Button type="link" icon={<EditOutlined />} onClick={() => openEditDrawer(record)}>
          Edit
        </Button>
      ),
    },
  ];

  return (
    <>
      <Card
        title={(
          <Flex alignItems="center" style={{ gap: 8 }}>
            <GlobalOutlined />
            <span>Meta Pricing (INR)</span>
            <Text type="secondary">
              Official Meta WhatsApp rates by country. Update here when Meta publishes new pricing.
            </Text>
          </Flex>
        )}
        extra={(
          <Flex gap={10}>
            <Input
              allowClear
              placeholder="Search country or market"
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ width: '100%' }}
            />
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreateDrawer}>
              Add New
            </Button>
          </Flex>
        )}
      >
        <Row justify="space-between" align="middle" style={{ marginBottom: 16, gap: '12px 16px' }}>
          <Col xs={24} md={16} lg={18}>

          </Col>

        </Row>

        <Table
          rowKey="id"
          loading={loading}
          columns={columns}
          dataSource={filteredRates}
          scroll={{ x: 1100 }}
          pagination={{ pageSize: 20, showSizeChanger: true }}
        />
      </Card>

      <CustomDrawer
        title={editingRecord ? 'Edit Meta Pricing' : 'Add Meta Pricing'}
        open={drawerOpen}
        onClose={closeDrawer}
        width={560}
        destroyOnClose
        footer={(
          <Space style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button onClick={closeDrawer}>Cancel</Button>
            <Button type="primary" loading={saving} onClick={handleSubmit}>
              {editingRecord ? 'Update' : 'Create'}
            </Button>
          </Space>
        )}
      >
        <Form form={form} layout="vertical" initialValues={DEFAULT_FORM_VALUES}>
          <Row gutter={ROW_GUTTER}>
            <Col span={12}>
              <Form.Item
                name="country_iso2"
                label="Country ISO2"
                rules={[
                  { required: true, message: 'Country ISO2 is required' },
                  { len: 2, message: 'Use 2-letter ISO code (e.g. IN)' },
                ]}
              >
                <Input
                  maxLength={2}
                  placeholder="IN"
                  disabled={!!editingRecord}
                  onChange={(e) => form.setFieldValue('country_iso2', e.target.value.toUpperCase())}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="currency"
                label="Currency"
                rules={[{ required: true, message: 'Currency is required' }]}
              >
                <Input maxLength={3} placeholder="INR" onChange={(e) => form.setFieldValue('currency', e.target.value.toUpperCase())} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="market_name" label="Market name">
            <Input placeholder="India, North America, etc." />
          </Form.Item>

          <Row gutter={ROW_GUTTER}>
            <Col span={12}>
              <Form.Item
                name="marketing_price"
                label="Marketing price"
                rules={[{ required: true, message: 'Marketing price is required' }]}
              >
                <InputNumber min={0} step={0.0001} precision={4} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="utility_price"
                label="Utility price"
                rules={[{ required: true, message: 'Utility price is required' }]}
              >
                <InputNumber min={0} step={0.0001} precision={4} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={ROW_GUTTER}>
            <Col span={12}>
              <Form.Item
                name="authentication_price"
                label="Authentication price"
                rules={[{ required: true, message: 'Authentication price is required' }]}
              >
                <InputNumber min={0} step={0.0001} precision={4} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="authentication_intl_price" label="Auth international price">
                <InputNumber min={0} step={0.0001} precision={4} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={ROW_GUTTER}>
            <Col span={12}>
              <Form.Item
                name="effective_from"
                label="Effective from"
                rules={[{ required: true, message: 'Effective date is required' }]}
              >
                <Input type="date" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="is_active" label="Active" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </CustomDrawer>
    </>
  );
};

export default MetaPricing;
